import React, { createContext, useContext, useState, useEffect } from 'react';
import { post_api_headers } from '../utils/Store';
import { useAtom } from 'jotai';
import useSWR from 'swr';
import { UserContext } from '../components/UserContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  return useContext(NotificationContext);
};

export const NotificationProvider = ({ children }) => {
  const { userProfile } = useContext(UserContext);
  const [unreadCount, setUnreadCount] = useState(0);
  const baseurl = process.env.REACT_APP_API_PROJECT_MANAGEMENT_API;
  const [postHeaders] = useAtom(post_api_headers);
  const idToken = sessionStorage.getItem('id_token');

  const [userId, setUserId] = useState(null);

  /**
   * Resolve userId from sessionStorage first, then context
   */
  useEffect(() => {
    const storedProfile = sessionStorage.getItem('userProfile');

    if (storedProfile) {
      try {
        const parsed = JSON.parse(storedProfile);
        if (parsed?.userId) {
          setUserId(parsed.userId);
          return;
        }
      } catch {
        // silent fallback to context
      }
    }

    if (userProfile?.userId) {
      setUserId(userProfile.userId);
    }
  }, [userProfile]);

  /**
   * SWR fetcher (no useless try/catch)
   */
  const fetcher = async (url) => {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...postHeaders,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch notifications: ${response.status}`);
    }

    return response.json();
  };

  const { data: newNotifications, mutate } = useSWR(
    userId ? `${baseurl}/api/notifications/user/${userId}/new` : null,
    fetcher,
    {
      refreshInterval: 20000,
      dedupingInterval: 5000,
      revalidateOnFocus: true,
      onSuccess: (data) => {
        setUnreadCount(data?.length || 0);
      },
    }
  );

  const refreshCount = () => {
    if (userId) {
      mutate();
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        updateUnreadCount: setUnreadCount,
        refreshCount,
        notifications: newNotifications || [],
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};